import { useEffect, useRef, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { KbError, clearSession, currentSession, refreshSession, logout, syncStoredSession } from './api/client'
import { canAccessPage, homeFor, loginUrl, loginReturnTo } from './api/navigation'
import SessionMonitor from './components/SessionMonitor'
import ChangePassword from './components/ChangePassword'
import { setPermissionPolicy } from './api/permissions'
import type { Session } from './api/client'
import AppShell from './shell/AppShell'
import { ReviewNotifications } from './components/ReviewNotifications'
import { can } from './api/permissions'
import TraceBanner from './components/TraceBanner'
import Library from './pages/Library'
import RevisionWorkbench from './pages/RevisionWorkbench'
import BaselineEditor from './pages/BaselineEditor'
import Review from './pages/Review'
import ProjectWorkspace from './pages/ProjectWorkspace'
import Favorites from './pages/Favorites'
import HistoricalMaterials from './pages/HistoricalMaterials'
import Accounts from './pages/Accounts'
import ModelCalls from './pages/ModelCalls'
import SignIn from './pages/SignIn'

export default function App() {
  const [session, setSession] = useState<Session | null>(currentSession())
  const navigate=useNavigate(),location=useLocation()
  const locationRef = useRef(location); locationRef.current = location
  const [loginRedirect,setLoginRedirect] = useState<{to:string;notice:string}|null>(null)
  const [passwordOpen,setPasswordOpen]=useState(false),[signingOut,setSigningOut]=useState(false),[accountError,setAccountError]=useState('')
  const [authorizationReady, setAuthorizationReady] = useState(false)
  const [authorizationError, setAuthorizationError] = useState('')
  useEffect(() => {
    if (!session) { setAuthorizationReady(false); setPermissionPolicy(null); return }
    let active = true
    const update = async () => {
      try {
        const result = await refreshSession()
        if (!active) return
        setPermissionPolicy(result.policy); setSession(result.session); setAuthorizationReady(true); setAuthorizationError('')
      } catch (error) { if (active) { setAuthorizationError(error instanceof Error ? error.message : '权限加载失败') } }
    }
    const expired = () => { const current=locationRef.current;clearSession(); setSession(null);setPasswordOpen(false);setPermissionPolicy(null);setAuthorizationReady(false);setLoginRedirect({to:loginUrl(current.pathname+current.search+current.hash),notice:'登录已失效或账号状态已变化，请重新登录。'}) }
    void update()
    const timer = window.setInterval(() => void update(), 30000)
    window.addEventListener('focus', update); window.addEventListener('kb:permissions-changed', update); window.addEventListener('kb:session-expired', expired)
    return () => { active = false; window.clearInterval(timer); window.removeEventListener('focus', update); window.removeEventListener('kb:permissions-changed', update); window.removeEventListener('kb:session-expired', expired) }
  }, [session?.sessionId])

  useEffect(()=>{
    const storage=(event:StorageEvent)=>{if(event.key!=='sr.token')return;setPermissionPolicy(null);setAuthorizationReady(false);setPasswordOpen(false);setLoginRedirect(null);setSession(syncStoredSession())}
    window.addEventListener('storage',storage);return()=>window.removeEventListener('storage',storage)
  },[])
  const roles=session?.roles ?? []
  const signedOut=(message:string)=>{clearSession();setPermissionPolicy(null);setAuthorizationReady(false);setPasswordOpen(false);setSession(null);setLoginRedirect({to:'/login',notice:message})}
  const signOut=async()=>{if(signingOut)return;setSigningOut(true);setAccountError('');try{await logout();signedOut('已退出登录')}catch(error){setAccountError(error instanceof KbError?error.message:'无法连接服务器，退出尚未完成，请检查网络后重试。')}finally{setSigningOut(false)}}

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<SignIn notice={loginRedirect?.notice ?? location.state?.notice} onSignIn={next=>{setLoginRedirect(null);setAuthorizationReady(false);setPermissionPolicy(null);setAccountError('');setSession(next);navigate(loginReturnTo(location.search) ?? '/', {replace:true})}} />} />
        <Route path="*" element={<Navigate to={loginRedirect?.to ?? loginUrl(location.pathname+location.search+location.hash)} replace state={loginRedirect?.notice || location.state?.notice ? {notice:loginRedirect?.notice ?? location.state.notice} : undefined}/>} />
      </Routes>
    )
  }
  if (!authorizationReady) return <div className="signin-page"><div className="panel signin-card"><div className="panel-body">{authorizationError || '正在加载账号权限…'}{authorizationError ? <button type="button" className="btn" onClick={() => window.location.reload()}>重试</button> : null}</div></div></div>
  if (location.pathname === '/login') return <Navigate to={loginReturnTo(location.search) ?? homeFor(roles)} replace />

  return (
    <ReviewNotifications key={session.sessionId} sessionId={session.sessionId} enabled={can(roles, 'revision.review')}>
    <AppShell
      roles={roles}
      principalId={session.principalId}
      onChangePassword={()=>setPasswordOpen(true)}
      signingOut={signingOut}
      onSignOut={()=>void signOut()}
      banner={<TraceBanner />}
    >
      <SessionMonitor session={session} onUpdate={setSession} />
      {authorizationError ? <div className="note warn" role="status">暂时无法校验登录状态，请检查网络；恢复后会自动重试。</div> : null}
      {accountError ? <div className="note danger" role="alert">{accountError}</div> : null}
      {passwordOpen ? <ChangePassword onClose={()=>setPasswordOpen(false)} onChanged={()=>signedOut('密码已修改，请使用新密码重新登录。')} /> : null}
      {location.pathname !== '/no-access' && !canAccessPage(roles,location.pathname) ? <Navigate to={homeFor(roles)} replace /> : <Routes>
        <Route path="/no-access" element={<div className="note">当前账号没有可访问的功能，请联系管理员。</div>}/>
        <Route path="/library" element={<Library roles={roles} />} />
        <Route path="/revisions/:revisionId/diff" element={<RevisionWorkbench roles={roles} />} />
        <Route path="/assets/:assetId/baseline-editor" element={<BaselineEditor roles={roles} />} />
        <Route path="/review" element={<Review roles={roles} />} />
        <Route path="/review/:reviewId" element={<Review roles={roles} />} />
        <Route path="/projects" element={<ProjectWorkspace roles={roles} />} />
        <Route path="/projects/:projectId" element={<ProjectWorkspace roles={roles} />} />
        <Route path="/kb" element={<Navigate to="/library?chat=1" replace />} />
        <Route path="/historical-materials" element={<HistoricalMaterials roles={roles} />} />
        <Route path="/favorites" element={<Favorites roles={roles} />} />
        <Route path="/accounts" element={<Accounts roles={roles} />} />
        <Route path="/models" element={<ModelCalls roles={roles} />} />
        <Route path="*" element={<Navigate to={homeFor(roles)} replace />} />
      </Routes>}
    </AppShell>
    </ReviewNotifications>
  )
}
